import { useMemo } from 'react';
import { useI18n, type MessageKey } from '../i18n';
import { formatIsraeliPhoneDisplay } from '../utils/phoneE164';
import { formatProfileDateOfBirthDisplay } from '../utils/profileDisplaySummary';
import {
  PRIVATE_CLIENT_USER_INFO_KEYS,
  incompletePrivateClientUserInfoKeys,
  type PrivateClientUserInfoKey,
} from '../utils/privateClientUserInfoProblems';
import { ProfileUserInfoValue } from './ProfileUserInfoValue';
import styles from './ProfileCompletionUserInfo.module.css';

const USER_INFO_LABEL_KEYS: Record<PrivateClientUserInfoKey, MessageKey> = {
  first_name: 'profile.field.firstName',
  last_name: 'profile.field.lastName',
  email: 'profile.field.email',
  phone_number: 'profile.field.cellPhone',
  birth_date: 'profile.field.dateOfBirth',
};

function pickString(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return '';
}

function displayUserInfoValue(
  key: PrivateClientUserInfoKey,
  payload: Record<string, unknown>,
  notSet: string,
): string {
  if (key === 'phone_number') {
    const phone = pickString(payload.phone_number) || pickString(payload.phone);
    return phone ? formatIsraeliPhoneDisplay(phone) : notSet;
  }
  if (key === 'birth_date') {
    const raw = pickString(payload.birth_date);
    return formatProfileDateOfBirthDisplay(raw) || notSet;
  }
  return pickString(payload[key]) || notSet;
}

type ProfileCompletionUserInfoProps = {
  payload: Record<string, unknown>;
};

export function ProfileCompletionUserInfo({ payload }: ProfileCompletionUserInfoProps) {
  const { t, language } = useI18n();
  const notSet = t('profile.field.notSet');
  const problems = useMemo(() => new Set(incompletePrivateClientUserInfoKeys(payload)), [payload]);

  return (
    <dl className={styles.list} dir={language === 'he' ? 'rtl' : 'ltr'}>
      {PRIVATE_CLIENT_USER_INFO_KEYS.map((key) => {
        const problem = problems.has(key);
        const ltr = key === 'email' || key === 'phone_number';
        return (
          <div key={key} className={styles.row}>
            <dt className={styles.label}>{t(USER_INFO_LABEL_KEYS[key])}</dt>
            <dd className={[styles.value, ltr ? styles.valueLtr : ''].filter(Boolean).join(' ')}>
              <ProfileUserInfoValue
                text={displayUserInfoValue(key, payload, notSet)}
                problem={problem}
              />
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
